import { ThingShadow } from '@cco/model';
import { createReducer, on } from '@ngrx/store';
import * as lodash from 'lodash';
import * as IotThingActions from './iot-thing.actions';

/**
 * Recursively remove null and empty elements from an object
 *
 * Based on https://stackoverflow.com/a/44808210
 */
function cleanDeep<T>(obj: T): T {
  function internalClean(obj1: any) {
    return lodash.transform(obj1, (result, value, key) => {
      const isCollection = lodash.isObject(value);
      const cleaned = isCollection ? internalClean(value) : value;

      if (isCollection && lodash.isEmpty(cleaned)) {
        return;
      }
      if (lodash.isNull(cleaned)) {
        return;
      }

      lodash.isArray(result) ? result.push(cleaned) : ((result as any)[key] = cleaned);
    });
  }

  return lodash.isObject(obj) ? internalClean(obj) : (obj as any);
}

export interface State {
  // eslint-disable-next-line @typescript-eslint/ban-types
  [thingName: string]: ThingShadow<object>;
}

export const reducer = createReducer<State>(
  {},
  on(IotThingActions.patchThingShadow, (state, action) => {
    // We can't patch what isn't there
    if (!state[action.thingName]) {
      return state;
    }

    const shadow = lodash.cloneDeep(state[action.thingName]);
    const shadowUpdate = {
      ...lodash.omitBy(
        {
          state: action.shadowUpdateAccepted.state,
          metadata: action.shadowUpdateAccepted.metadata,
        },
        lodash.isNil,
      ),
      timestamp: action.shadowUpdateAccepted.timestamp,
      version: action.shadowUpdateAccepted.version,
    } as ThingShadow<Record<string, unknown>>;

    if (!shadow?.version) {
      // We do not have a current full shadow.  Ignoring update.
      return state;
    }

    if (shadowUpdate.version < shadow.version + 1) {
      return state;
    }

    if (shadowUpdate.version > shadow.version + 1) {
      return state;
    }
    const newShadow = cleanDeep(
      lodash.mergeWith(shadow, shadowUpdate, (objValue, srcValue) => {
        // Overwrite arrays
        if (Array.isArray(srcValue)) {
          return srcValue;
        }

        // This needs to return undefined so mergeWith uses its default behavior.
        // Returning null will override that behavior.
        return;
      }),
    ) as ThingShadow<Record<string, unknown>>;

    return { ...state, [action.thingName]: newShadow };
  }),
  on(IotThingActions.upsertThingShadow, (state, action) => {
    const storeVersion = state[action.thingName]?.version;
    if (storeVersion && storeVersion > action.shadow.version) {
      // API data may be delayed; don't overwrite if the update is stale
      return state;
    }
    return { ...state, [action.thingName]: action.shadow };
  }),
);
